/**
 * Liveness / PAD — standalone HyperVerge liveness check.
 * Separate from Face Match and Video KYC.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, ScanFace, Camera, ShieldCheck, ShieldOff } from "lucide-react";
import ProviderBadge from "../components/ProviderBadge";
import { validateImage } from "../utils/fileValidation";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LivenessPage() {
  const [provider, setProvider] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/providers/didit`, { withCredentials: true })
      .then(r => setProvider(r.data))
      .catch(() => setProvider({ mode: "mock", label: "Didit · MOCK" }));
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateImage(f);
    if (err) return toast.error(err);
    setSelfie(f); setPreview(URL.createObjectURL(f)); setResult(null);
  };

  const onCheck = async () => {
    if (!selfie) return toast.error("Capture / upload a selfie first");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("selfie", selfie);
      const res = await axios.post(`${API}/v1/kyc/liveness`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(res.data.is_live ? "LIVE" : "NOT LIVE");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.error || "Liveness check failed"));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="liveness-page" style={{ maxWidth: 800, margin: "0 auto" }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
            Liveness / PAD
          </h1>
          <p style={{ color: "#64748b", marginTop: 6, fontSize: 13 }}>
            Passive Presentation-Attack Detection. Single-frame liveness with deepfake / mask / replay analysis.
          </p>
        </div>
        {provider && <ProviderBadge mode={provider.mode} label={provider.label} slug="didit" />}
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 22 }}>
        <input type="file" accept="image/jpeg,image/png" onChange={onFile}
               data-testid="liveness-file-input" style={{ color: "#94a3b8", fontSize: 12 }} />
        {preview && (
          <div style={{ marginTop: 16 }}>
            <img src={preview} alt="selfie" style={{ maxHeight: 220, borderRadius: 8 }} />
          </div>
        )}
        <div>
          <button onClick={onCheck} disabled={loading || !selfie} data-testid="liveness-check-btn"
            style={{
              marginTop: 14, padding: "10px 22px", borderRadius: 8,
              background: "#2563eb", color: "#fff", border: "none",
              fontSize: 13, fontWeight: 700, cursor: selfie ? "pointer" : "not-allowed",
              display: "inline-flex", alignItems: "center", gap: 8,
              opacity: selfie ? 1 : 0.6,
            }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ScanFace size={14} />} Run Liveness
          </button>
        </div>

        {result && (
          <div data-testid="liveness-result" style={{
            marginTop: 22, padding: 16, borderRadius: 10,
            background: result.is_live ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
            border: `1px solid ${result.is_live ? "#10b98144" : "#ef444444"}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              {result.is_live ? <ShieldCheck color="#10b981" size={18} /> : <ShieldOff color="#ef4444" size={18} />}
              <span style={{ fontWeight: 700, fontSize: 15,
                              color: result.is_live ? "#10b981" : "#ef4444" }}>
                {result.is_live ? "LIVE" : "SPOOF / NOT LIVE"}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#cbd5e1", display: "grid", gap: 4 }}>
              <div><span style={{ color: "#64748b" }}>Score:</span> {(result.score * 100).toFixed(1)}%</div>
              {result.spoof_type && <div><span style={{ color: "#64748b" }}>Spoof type:</span> {result.spoof_type}</div>}
              <div style={{ fontSize: 10, color: "#475569", marginTop: 6 }}>txn: {result.transaction_id}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
